import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Copy, Download, Check, Music } from "lucide-react";
import { toast } from "sonner";
import { JumpToLatest } from "@/components/JumpToLatest";

export const TranscriptPanel = ({ transcript, isRecording, isTranscribing, sessionTitle, startTime, endTime }) => {
  const scrollRef = useRef(null);
  const [copiedIndex, setCopiedIndex] = React.useState(null);

  const stickToBottomRef = useRef(true);
  const lastText = transcript.length ? transcript[transcript.length - 1].text : "";

  // Auto-scroll only when new text arrives and the user is already at the bottom
  useEffect(() => {
    if (scrollRef.current && stickToBottomRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [transcript.length, lastText]);

  const [showJump, setShowJump] = useState(false);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    stickToBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };

  const jumpToLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit'
    });
  };

  const formatTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const exportSegment = (item, index) => {
    const text = `[${item.timestamp}]\n${item.text}`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `segment-${index + 1}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Segment exported");
  };

  const downloadAudio = (item, index) => {
    if (item.id) {
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/transcriptions/${item.id}/audio`, '_blank');
      return;
    }
    if (!item.audioBlobUrl) {
      toast.error("Audio not available for this segment");
      return;
    }
    
    const a = document.createElement('a');
    a.href = item.audioBlobUrl;
    a.download = item.audioFileName || `audio-segment-${index + 1}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    toast.success("Audio downloaded");
  };

  return (
    <Card className="bg-deepspace-surface border-l border-deepspace-border h-full flex flex-col overflow-hidden" data-testid="transcript-panel">
      <div className="p-6 border-b border-deepspace-border flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex-1">
            <h2 className="text-2xl font-semibold font-outfit text-[#EDEDED]">Live Transcript</h2>
            {sessionTitle && (
              <p className="text-sm text-[#A1A1AA] mt-1">{sessionTitle}</p>
            )}
          </div>
          {isTranscribing && (
            <div className="flex items-center gap-2 text-volt-blue">
              <div className="w-2 h-2 bg-volt-blue rounded-full animate-pulse"></div>
              <span className="text-xs font-medium">Processing...</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-6 text-xs text-[#A1A1AA] mt-3 border-t border-deepspace-border pt-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-volt-blue" />
            <div>
              <span className="text-[#52525B]">Start:</span> {formatTime(startTime)}
            </div>
          </div>
          {endTime && (
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-recording-red" />
              <div>
                <span className="text-[#52525B]">End:</span> {formatTime(endTime)}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-[#52525B]">Segments:</span>
            <span className="text-volt-blue font-semibold">{transcript.length}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        <JumpToLatest visible={showJump && transcript.length > 0} onClick={jumpToLatest} testId="jump-to-latest-btn" />
        <div className="h-full transcript-container p-6" ref={scrollRef} onScroll={handleScroll} style={{ overflowY: 'scroll' }} data-testid="transcript-scroll-area">
          {transcript.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center" data-testid="transcript-empty-state">
              <div className="text-[#52525B] mb-4">
                <svg className="w-20 h-20 mx-auto mb-4 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </div>
              <p className="text-[#A1A1AA] text-lg">
                {isRecording ? "Listening for audio..." : "Start recording to see transcript"}
              </p>
              <p className="text-[#52525B] text-sm mt-2">
                Transcripts are generated every ~90 seconds
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {transcript.map((item, index) => (
                <div
                  key={index}
                  className="bg-deepspace-bg border border-deepspace-border rounded-lg p-5 transition-all duration-300 hover:border-volt-blue/50 animate-in fade-in slide-in-from-bottom-2"
                  data-testid={`transcript-item-${index}`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <Clock className="w-4 h-4 text-volt-blue" />
                      <span className="font-jetbrains text-base text-volt-blue font-semibold">
                        {item.timestamp}
                      </span>
                      <span className="text-sm text-[#A1A1AA]">Segment {index + 1}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        className="h-9 px-4 text-sm bg-volt-blue hover:bg-volt-blue/80 text-white transition-all"
                        onClick={() => copyToClipboard(item.text, index)}
                        data-testid={`copy-segment-${index}`}
                      >
                        {copiedIndex === index ? (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4 mr-2" />
                            Copy
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        className="h-9 px-4 text-sm bg-[#10B981] hover:bg-[#10B981]/80 text-white transition-all"
                        onClick={() => exportSegment(item, index)}
                        data-testid={`export-segment-${index}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Export
                      </Button>
                      <Button
                        size="sm"
                        className="h-9 px-4 text-sm bg-[#8B5CF6] hover:bg-[#8B5CF6]/80 text-white transition-all"
                        onClick={() => downloadAudio(item, index)}
                        data-testid={`download-audio-${index}`}
                      >
                        <Music className="w-4 h-4 mr-2" />
                        Audio
                      </Button>
                    </div>
                  </div>
                  <p
                    className="mt-3 text-[#EDEDED] text-sm leading-relaxed whitespace-pre-wrap"
                    data-testid={`transcript-text-${index}`}
                  >
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default TranscriptPanel;
